import { nanoid } from 'nanoid';

import type {
  Message,
  Part,
  Task,
  TaskArtifactUpdateEvent,
  TaskStatusUpdateEvent,
} from '@a2a-js/sdk';
import type { ChatMessage } from '@cast/types';

import { mapPartsToMessages } from './mapPartsToMessages';

// ---------------------------------------------------------------------------
// Stream event narrowing
// ---------------------------------------------------------------------------

type A2AEventKind = 'message' | 'task' | 'status-update' | 'artifact-update';

const createGuard = <T>(expectedKind: A2AEventKind) => {
  return (event: unknown): event is T =>
    typeof event === 'object' &&
    event !== null &&
    'kind' in event &&
    (event as Record<string, unknown>).kind === expectedKind;
};

const isMessage = createGuard<Message>('message');
const isTask = createGuard<Task>('task');
const isStatusUpdate = createGuard<TaskStatusUpdateEvent>('status-update');
const isArtifactUpdate =
  createGuard<TaskArtifactUpdateEvent>('artifact-update');

const extractTextFromParts = (
  parts: Part[] | undefined
): string | undefined => {
  if (!parts?.length) return undefined;
  const text = parts
    .filter((p): p is Extract<Part, { kind: 'text' }> => p.kind === 'text')
    .map((p) => p.text)
    .join('\n')
    .trim();
  return text || undefined;
};

// Terminal TaskState values that should stop the loop. Any other state
// ('submitted', 'working', 'unknown') keeps the loop waiting for more events.
const TERMINAL_TASK_STATES = new Set<TaskStatusUpdateEvent['status']['state']>([
  'completed',
  'canceled',
  'failed',
  'rejected',
  'input-required',
  'auth-required',
]);

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export type A2AStreamProcessState = {
  taskId?: string;
  accumulatedParts: Part[];
  finalized: boolean;
};

export const createA2AStreamProcessState = (): A2AStreamProcessState => ({
  accumulatedParts: [],
  finalized: false,
});

type StreamProcessorHandlers = {
  /** Called with the orchestrator's reasoning text on `working` events.
   *  Caller is responsible for guarding against superseded requests. */
  setThinkingText: (text: string | undefined) => void;
  /** Called once the stream resolves to a final agent reply. Caller decides
   *  which chat (if any) should receive the messages. */
  appendMessages: (msgs: ChatMessage | ChatMessage[]) => void;
  /** Called exactly once per stream, after appendMessages. */
  trackCompleted: () => void;
};

/**
 * Consumes an A2A message stream and drives the supplied handlers. Resolves
 * normally when the stream ends or a terminal event is observed; throws on
 * transport/SDK errors so the caller can decide how to surface them.
 *
 * Pass a shared `state` across reconnect attempts so `taskId` and accumulated
 * artifact parts survive mid-stream TCP drops (see `resubscribeTask`).
 *
 * Finalization is idempotent: if a terminal event arrives *and* the stream
 * still has buffered events, only the first wins. This protects against
 * double-rendering on streams that emit e.g. `completed` right before closing.
 */
export const processA2AStream = async (
  stream: AsyncIterable<unknown>,
  handlers: StreamProcessorHandlers,
  state: A2AStreamProcessState = createA2AStreamProcessState()
): Promise<A2AStreamProcessState> => {
  const { setThinkingText, appendMessages, trackCompleted } = handlers;

  // Per-invocation: a ref would just need explicit reset on every call.
  const clearThinking = () => setThinkingText(undefined);

  const rememberTaskId = (taskId: string | undefined) => {
    if (taskId && !state.taskId) {
      state.taskId = taskId;
    }
  };

  const finalizeWithParts = (parts: Part[]) => {
    if (state.finalized) return;
    state.finalized = true;
    clearThinking();
    if (parts.length === 0) {
      appendMessages({
        id: nanoid(),
        role: 'agent',
        text: '⚠️ Sorry, looks like the agent responded with nothing. Try asking again.',
      });
    } else {
      appendMessages(mapPartsToMessages(parts));
    }
    trackCompleted();
  };

  for await (const event of stream) {
    if (isMessage(event)) {
      // Direct terminal message — treat the whole interaction as done.
      finalizeWithParts(event.parts);
      break;
    }

    if (isTask(event)) {
      // Initial task announcement. Keep the id so a mid-stream disconnect
      // can call tasks/resubscribe. The orchestrator will follow with
      // status-update (working) and artifact-update events.
      rememberTaskId(event.id);
      continue;
    }

    if (isStatusUpdate(event)) {
      rememberTaskId(event.taskId);
      const { state: taskState } = event.status;

      if (taskState === 'working') {
        const reasoning = extractTextFromParts(event.status.message?.parts);
        if (reasoning) setThinkingText(reasoning);
        continue;
      }

      if (taskState === 'completed') {
        finalizeWithParts(state.accumulatedParts);
        break;
      }

      if (TERMINAL_TASK_STATES.has(taskState)) {
        // failed / canceled / rejected / input-required / auth-required.
        // Prefer the orchestrator-provided status message so charts and
        // follow-ups survive the trip through mapPartsToMessages; fall
        // back to whatever artifacts we already accumulated.
        const failureParts = event.status.message?.parts;
        if (failureParts?.length) {
          finalizeWithParts(failureParts);
        } else {
          finalizeWithParts(state.accumulatedParts);
        }
        break;
      }

      // Non-terminal informational state (e.g. 'submitted'). Keep waiting.
      continue;
    }

    if (isArtifactUpdate(event)) {
      rememberTaskId(event.taskId);
      const parts = event.artifact.parts;
      if (parts?.length) state.accumulatedParts.push(...parts);
    }
  }

  // Stream ended without an explicit terminal event. Finalize whatever
  // artifacts we managed to accumulate so we never leave the user staring
  // at a forever-spinning thinking bubble.
  finalizeWithParts(state.accumulatedParts);
  return state;
};
